import { SEO } from "@/components/seo/SEO";
import { generateBreadcrumbSchema, generateMedicalWebPageSchema, generateArticleSchema } from "@/lib/seoSchemas";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useContinueLearning } from "@/hooks/useContinueLearning";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ReactMarkdown from "react-markdown";
import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSearchParams } from "react-router-dom";
import { TopicBreadcrumb } from "@/components/navigation/TopicBreadcrumb";
import { UniversalBreadcrumb } from "@/components/navigation/UniversalBreadcrumb";
import { TopicSwitcher } from "@/components/navigation/TopicSwitcher";
import { SmartBreadcrumb } from "@/components/navigation/SmartBreadcrumb";
import { SmartNextSteps } from "@/components/navigation/SmartNextSteps";
import { 
  Search, 
  FileText, 
  Clock, 
  BookOpen, 
  Filter,
  Stethoscope,
  Heart,
  Brain,
  Eye,
  Activity,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  Home,
  List,
  ChevronUp,
  ChevronDown
} from "lucide-react";

const sb = supabase as unknown as SupabaseClient<any>;

const Notes = () => {
  const { updateLearningState } = useContinueLearning();
  const [q, setQ] = useState("");
  const [topicId, setTopicId] = useState<string | undefined>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [noteStartTime, setNoteStartTime] = useState<Date | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);
  
  // Initialize topic from URL params
  useEffect(() => {
    const urlTopicId = searchParams.get('topic');
    if (urlTopicId) {
      setTopicId(urlTopicId);
    }
  }, [searchParams]);

  const { data: topics = [], isLoading: loadingTopics } = useQuery({
    queryKey: ["topics"],
    queryFn: async () => {
      const { data, error } = await sb.from("topics").select("id,name").order("name", { ascending: true });
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  type NoteRow = { id: string; topic_id: string | null; title: string; content: string; estimated: number };
  const { data: notes = [], isLoading: loadingNotes } = useQuery({
    queryKey: ["notes"],
    queryFn: async () => {
      const { data, error } = await sb
        .from("notes")
        .select("id, topic_id, title, content, estimated")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data as NoteRow[]).map((n) => ({
        id: n.id,
        topicId: n.topic_id || undefined,
        title: n.title,
        content: n.content,
        estimated: n.estimated ?? 5,
      }));
    },
  });

  const filtered = useMemo(
    () =>
      notes.filter(
        (n) => (!topicId || n.topicId === topicId) && (n.title.toLowerCase().includes(q.toLowerCase()) || n.content.toLowerCase().includes(q.toLowerCase()))
      ),
    [notes, q, topicId]
  );
  
  const [active, setActive] = useState(filtered[0]?.id);
  const activeNote = filtered.find((n) => n.id === active) || filtered[0];
  
  // Navigation helpers
  const currentIndex = filtered.findIndex(n => n.id === active);
  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < filtered.length - 1;
  
  const goToPrevious = () => {
    if (canGoPrevious) {
      setActive(filtered[currentIndex - 1].id);
    }
  };
  
  const goToNext = () => {
    if (canGoNext) {
      setActive(filtered[currentIndex + 1].id);
    }
  };
  
  const goToFirst = () => {
    if (filtered.length > 0) {
      setActive(filtered[0].id);
    }
  };
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle shortcuts when not typing in input fields
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      
      switch (e.key) {
        case 'ArrowUp':
        case 'k':
          e.preventDefault();
          goToPrevious();
          break;
        case 'ArrowDown':
        case 'j':
          e.preventDefault();
          goToNext();
          break;
        case 'Home':
        case 'g':
          e.preventDefault();
          goToFirst();
          break;
        case '/':
          e.preventDefault();
          // Focus search input
          (document.querySelector('input[placeholder*="Search"]') as HTMLInputElement)?.focus();
          break;
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, filtered.length]);
  
  // Auto-select first note when filters change
  useEffect(() => {
    if (filtered.length > 0 && (!active || !filtered.find(n => n.id === active))) {
      setActive(filtered[0].id);
    }
  }, [filtered, active]);

  // Update learning state when active note or topic changes
  useEffect(() => {
    if (activeNote) {
      const currentTopic = topics.find(t => t.id === topicId);
      updateLearningState({
        path: `/notes${topicId ? `?topic=${topicId}` : ''}`,
        type: 'notes',
        title: activeNote.title,
        topicId,
        topicName: currentTopic?.name,
      });
      
      // Start tracking time on this note
      setNoteStartTime(new Date());
      
      // Cleanup: check if task should be completed when note changes
      return () => {
        if (noteStartTime && activeNote && userId && activeNote.topicId) {
          const timeSpent = Math.floor((new Date().getTime() - noteStartTime.getTime()) / 60000);
          if (timeSpent >= activeNote.estimated * 0.5) {
            supabase.functions.invoke('update-task-progress', {
              body: {
                activityType: 'review_notes',
                topicId: activeNote.topicId,
                sessionDate: new Date().toISOString().split('T')[0],
                durationMinutes: timeSpent,
                notesReviewed: 1
              }
            }).catch(err => console.error('Error updating study plan task:', err));
          }
        }
      };
    }
  }, [activeNote, topicId, topics, updateLearningState]);

  const getTopicIcon = (topicName: string) => {
    const lower = topicName.toLowerCase();
    if (lower.includes('cardio') || lower.includes('heart')) return Heart;
    if (lower.includes('neuro') || lower.includes('brain')) return Brain;
    if (lower.includes('ophthal') || lower.includes('eye')) return Eye;
    if (lower.includes('vital') || lower.includes('monitor')) return Activity;
    return Stethoscope;
  };

  const getTopicForNote = (noteTopicId: string | undefined) => {
    return topics.find(t => t.id === noteTopicId);
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  
  const schemas: Array<Record<string, any>> = [
    generateBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Notes", path: "/notes" }
    ], origin),
    generateMedicalWebPageSchema(
      "Medical Study Notes",
      "Comprehensive medical study notes covering all specialties. Organized by topic with search and filtering for efficient studying.",
      `${origin}/notes`
    )
  ];

  // Add article schema if viewing a specific note
  if (activeNote) {
    schemas.push(
      generateArticleSchema(
        activeNote.title,
        activeNote.content.substring(0, 200),
        `${origin}/notes`
      )
    );
  }

  return (
    <div className="space-y-6 pb-24 animate-fade-in">
      <SEO 
        title="Medical Study Notes - Comprehensive Review by Specialty"
        description="Access comprehensive medical study notes organized by specialty. Search and filter through detailed medical content covering all major topics for medical students."
        path="/notes"
        keywords="medical notes, medical study guide, medical student notes, medical specialty notes, clinical notes, medical review notes"
        schema={schemas}
        type={activeNote ? "article" : "website"}
      />
      
      {/* Smart Breadcrumb Navigation */}
      <SmartBreadcrumb 
        currentPath="/notes"
        currentTopicId={topicId}
        currentMode="notes"
      />
      
      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2 flex-1">
            <h1 className="text-3xl font-bold gradient-text">Medical Notes</h1>
            <p className="text-muted-foreground">
              Access comprehensive medical knowledge organized by specialty and topic.
            </p>
          </div>
          
          {/* Topic Switcher */}
          <div className="hidden md:block">
            <TopicSwitcher
              currentTopicId={topicId}
              onTopicChange={(newTopicId) => {
                setTopicId(newTopicId);
                if (newTopicId) {
                  setSearchParams({ topic: newTopicId });
                } else {
                  setSearchParams({});
                }
              }}
              contentType="notes"
            />
          </div>
        </div>
        
        {/* Mobile Topic Switcher */}
        <div className="md:hidden">
          <TopicSwitcher
            currentTopicId={topicId}
            onTopicChange={(newTopicId) => {
              setTopicId(newTopicId);
              if (newTopicId) {
                setSearchParams({ topic: newTopicId });
              } else {
                setSearchParams({});
              }
            }}
            contentType="notes"
          />
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="medical-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Search & Filter</h2>
          <Badge variant="outline" className="ml-auto text-xs">
            Press / to search, ↑↓ or j/k to navigate
          </Badge>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Search medical notes..." 
              value={q} 
              onChange={(e) => setQ(e.target.value)}
              className="pl-10 medical-input"
            />
          </div>
          <Select value={topicId} onValueChange={(value) => setTopicId(value === "all" ? undefined : value)}>
            <SelectTrigger className="w-full sm:w-[200px] medical-input">
              <SelectValue placeholder="All specialties" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All specialties</SelectItem>
              {topics.filter(t => t.id && t.id.trim() !== '').map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Stats */}
        <div className="flex items-center gap-4 mt-4 pt-4 border-t">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <FileText className="h-4 w-4" />
            <span>{filtered.length} notes found</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>{filtered.reduce((acc, note) => acc + note.estimated, 0)} min total reading</span>
          </div>
        </div>
      </div>

      {(loadingTopics || loadingNotes) && (
        <div className="medical-card p-12 text-center">
          <div className="flex items-center justify-center gap-3">
            <div className="w-6 h-6 animate-spin border-2 border-primary border-t-transparent rounded-full" />
            <span className="text-muted-foreground">Loading medical notes...</span>
          </div>
        </div>
      )}

      {!loadingTopics && !loadingNotes && (
        <div className="grid gap-6 lg:grid-cols-[400px,1fr]">
          {/* Notes List */}
          <div className="space-y-3">
            <div className="medical-card p-4">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Available Notes</h3>
                <Badge variant="secondary" className="ml-auto">
                  {filtered.length}
                </Badge>
              </div>
              
              {/* Quick Navigation */}
              {filtered.length > 1 && (
                <div className="flex items-center gap-2 mb-4 p-2 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <List className="h-3 w-3" />
                    {currentIndex + 1} of {filtered.length}
                  </div>
                  <div className="flex items-center gap-1 ml-auto">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={goToPrevious}
                      disabled={!canGoPrevious}
                      className="h-7 w-7 p-0"
                      title="Previous note (↑ or k)"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={goToNext}
                      disabled={!canGoNext}
                      className="h-7 w-7 p-0"
                      title="Next note (↓ or j)"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={goToFirst}
                      disabled={currentIndex === 0}
                      className="h-7 w-7 p-0"
                      title="First note (Home or g)"
                    >
                      <Home className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="space-y-2 max-h-[70vh] overflow-y-auto">
                {filtered.length > 0 ? filtered.map((note) => {
                  const topic = getTopicForNote(note.topicId);
                  const TopicIcon = topic ? getTopicIcon(topic.name) : FileText;
                  const isActive = active === note.id;
                  
                  return (
                    <Button
                      key={note.id}
                      variant={isActive ? "default" : "ghost"}
                      className={`w-full justify-start h-auto p-4 hover-lift ${
                        isActive ? 'medical-card-active' : 'medical-card-hover'
                      }`}
                      onClick={() => setActive(note.id)}
                    >
                      <div className="flex items-start gap-3 w-full text-left">
                        <TopicIcon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${isActive ? 'text-primary-foreground' : 'text-primary'}`} />
                        <div className="flex-1 min-w-0">
                          <div className={`font-medium truncate ${isActive ? 'text-primary-foreground' : 'text-foreground'}`}>
                            {note.title}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {topic && (
                              <Badge 
                                variant={isActive ? "secondary" : "outline"} 
                                className="text-xs medical-badge"
                              >
                                {topic.name}
                              </Badge>
                            )}
                            <div className={`flex items-center gap-1 text-xs ${isActive ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                              <Clock className="h-3 w-3" />
                              {note.estimated} min
                            </div>
                          </div>
                        </div>
                      </div>
                    </Button>
                  );
                }) : (
                  <div className="text-center p-8">
                    <FileText className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                    <h3 className="font-medium mb-1">No notes found</h3>
                    <p className="text-sm text-muted-foreground">
                      Try adjusting your search or filter criteria
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Note Content */}
          <div className="medical-card">
            {activeNote ? (
              <>
                <CardHeader className="space-y-4">
                  {/* Navigation breadcrumb */}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Home className="h-3 w-3" />
                    <span>Notes</span>
                    {(() => {
                      const topic = getTopicForNote(activeNote.topicId);
                      if (topic) {
                        return (
                          <>
                            <ChevronRight className="h-3 w-3" />
                            <span>{topic.name}</span>
                          </>
                        );
                      }
                      return null;
                    })()}
                    <ChevronRight className="h-3 w-3" />
                    <span className="text-foreground font-medium truncate">{activeNote.title}</span>
                  </div>
                  
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-2">
                      <CardTitle className="text-2xl gradient-text leading-tight">
                        {activeNote.title}
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        {(() => {
                          const topic = getTopicForNote(activeNote.topicId);
                          if (topic) {
                            const TopicIcon = getTopicIcon(topic.name);
                            return (
                              <Badge variant="secondary" className="medical-badge">
                                <TopicIcon className="h-3 w-3 mr-1" />
                                {topic.name}
                              </Badge>
                            );
                          }
                          return null;
                        })()}
                        <Badge variant="outline" className="medical-badge">
                          <Clock className="h-3 w-3 mr-1" />
                          {activeNote.estimated} min read
                        </Badge>
                      </div>
                    </div>
                    <Activity className="h-8 w-8 text-primary/60 flex-shrink-0" />
                  </div>
                </CardHeader>
                
                <Separator />
                
                {/* Navigation Controls */}
                {filtered.length > 1 && (
                  <div className="flex items-center justify-between px-6 py-3 border-b">
                    <Button
                      variant="ghost"
                      onClick={goToPrevious}
                      disabled={!canGoPrevious}
                      className="hover-lift"
                    >
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      Previous
                    </Button>
                    
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{currentIndex + 1} of {filtered.length} notes</span>
                    </div>
                    
                    <Button
                      variant="ghost"
                      onClick={goToNext}
                      disabled={!canGoNext}
                      className="hover-lift"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                )}
                
                <CardContent className="pt-6">
                  <article className="prose prose-neutral max-w-none dark:prose-invert prose-lg prose-headings:gradient-text prose-headings:font-semibold prose-p:text-foreground/90 prose-li:text-foreground/90 prose-strong:text-primary prose-code:text-primary prose-code:bg-primary/10 prose-code:px-1 prose-code:py-0.5 prose-code:rounded">
                    <ReactMarkdown>{activeNote.content}</ReactMarkdown>
                  </article>
                </CardContent>
                
                {/* Bottom Navigation for Mobile */}
                {filtered.length > 1 && (
                  <div className="flex items-center justify-between p-4 border-t bg-muted/20 lg:hidden">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToPrevious}
                      disabled={!canGoPrevious}
                      className="hover-lift"
                    >
                      <ArrowUp className="h-4 w-4 mr-1" />
                      Prev
                    </Button>
                    
                    <div className="text-xs text-muted-foreground">
                      {currentIndex + 1}/{filtered.length}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToNext}
                      disabled={!canGoNext}
                      className="hover-lift"
                    >
                      Next
                      <ArrowDown className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center p-12 text-center min-h-[400px]">
                <div className="medical-icon-bg p-4 rounded-full mb-4">
                  <Stethoscope className="h-12 w-12 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Select a Medical Note</h3>
                <p className="text-muted-foreground mb-4">
                  Choose a note from the list to view its content
                </p>
                <Button 
                  variant="outline" 
                  onClick={() => filtered.length > 0 && setActive(filtered[0].id)}
                  disabled={filtered.length === 0}
                  className="hover-lift"
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  View First Note
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Smart Next Steps */}
      {activeNote && (
        <div className="mt-8">
          <SmartNextSteps />
        </div>
      )}
    </div>
  );
};

export default Notes;
